import writeExcelFile from 'write-excel-file/browser';
import type { Cell, SheetData } from 'write-excel-file/browser';

export type ExcelExportRow = Record<string, unknown>;

const toExcelCell = (value: unknown): Cell => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return null;
    }

    return {
      value,
      type: Number,
      format: '0',
    };
  }

  if (typeof value === 'string' || typeof value === 'boolean') {
    return value;
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return {
      value,
      type: Date,
      format: '0',
    };
  }

  // The previous SheetJS export produced blank cells for nested objects,
  // arrays, and unsupported values. Preserve that behaviour explicitly.
  return null;
};

export const createExcelSheetData = (
  rows: readonly ExcelExportRow[]
): SheetData => {
  const headers: string[] = [];
  const headerSet = new Set<string>();

  rows.forEach((row) => {
    Object.keys(row).forEach((header) => {
      if (!headerSet.has(header)) {
        headerSet.add(header);
        headers.push(header);
      }
    });
  });

  if (headers.length === 0) {
    return [];
  }

  return [
    headers,
    ...rows.map((row) => headers.map((header) => toExcelCell(row[header]))),
  ];
};

export const downloadExcelFile = async (
  rows: readonly ExcelExportRow[],
  fileName: string
): Promise<void> => {
  await writeExcelFile(createExcelSheetData(rows), {
    sheet: 'data',
  }).toFile(`${fileName}.xlsx`);
};
